import { Check } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { minutesFor, tourSummaryKey, tourTitleKey } from '../registry';
import { useTour } from '../tour-context';
import type { TourCardStatus, TourDefinition } from '../types';
import { useTourReadiness } from '../use-tour-readiness';

const STATUS_TONE: Record<TourCardStatus, string> = {
  'not-started': '',
  done: 'border-transparent bg-success/15 text-success',
  updated: 'border-transparent bg-primary/10 text-primary',
  skipped: 'text-muted-foreground',
};

export interface HelpTourCardProps {
  definition: TourDefinition;
  onStart: (definition: TourDefinition) => void;
  starting: boolean;
}

/** One tour on the Help and tours page: what it teaches, how long, its status and Start. */
export function HelpTourCard({ definition, onStart, starting }: HelpTourCardProps) {
  const tour = useTour();
  const { t } = useTranslation(tour.namespace);
  const readiness = useTourReadiness(definition);
  const status = tour.statusOf(definition);
  const steps = tour.stepsFor(definition).length;
  const title = t(tourTitleKey(definition.id), tour.values);
  const action = t(`page.action.${status}`);
  const Icon = definition.icon;
  const reasonId = `tour-reason-${definition.id}`;
  const reason =
    readiness.blocker === 'needs-store'
      ? t('page.needsStore')
      : readiness.blocker === 'needs-record' && definition.resolve
        ? t('page.needsRecord', { thing: t(definition.resolve.thing) })
        : null;

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-start gap-3 px-5">
        <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
          <Icon aria-hidden className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <CardTitle className="text-[15px] leading-snug">
            <h3>{title}</h3>
          </CardTitle>
          <Badge variant="outline" className={cn('w-fit gap-1', STATUS_TONE[status])}>
            {status === 'done' ? <Check aria-hidden className="size-3" /> : null}
            {t(`page.status.${status}`)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-1 px-5">
        <p className="text-sm">{t(tourSummaryKey(definition.id))}</p>
        <p className="text-muted-foreground text-xs">
          {t('page.meta', { count: steps, minutes: minutesFor(steps + 1) })}
        </p>
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-1.5 px-5">
        <Button
          type="button"
          size="sm"
          variant={status === 'done' ? 'outline' : 'default'}
          disabled={reason !== null || readiness.isPending || starting}
          aria-label={t('page.actionLabel', { action, title })}
          aria-describedby={reason ? reasonId : undefined}
          onClick={() => onStart(definition)}
        >
          {readiness.isPending ? t('page.checking') : action}
        </Button>
        {reason ? (
          <p id={reasonId} className="text-muted-foreground text-xs">
            {reason}
          </p>
        ) : null}
      </CardFooter>
    </Card>
  );
}
